<template>
  <div
    :style="{ gap: `${tagGap}px` }"
    :class="[bem.b(), bem.is(size), bem.is(disabled ? 'disabled' : '')]"
  >
    <template v-for="(tag, idx) in newTags" :key="idx">
      <a-checkable-tag
        :style="tagMaxWidth ? { maxWidth: `${tagMaxWidth}px` } : {}"
        :class="[bem.e('tag-content'), bem.is(isChecked(tag) ? 'checked' : '')]"
        :checked="isChecked(tag)"
        @change="(checked) => handleCheck(tag, checked)"
      >
        <div :class="bem.e('tag')">
          <span>{{ tag[labelKey] || tag[valueKey] }}</span>
          <ll-icon
            v-if="showCloseIcon(idx)"
            icon-name="icon-close2"
            @click="handleClose(tag)"
          />
        </div>
      </a-checkable-tag>
    </template>
    <ll-tag v-if="!enableAdd && newTags.length === 0" shape="round">
      {{ emptyText || '暂无数据' }}
    </ll-tag>
    <!-- 新增标签 -->
    <template v-if="enableAdd">
      <ll-tag
        v-if="!inputVisible"
        shape="round"
        :icon="{
          iconName: 'icon-add',
        }"
        :class="[bem.e('add-tag'), bem.is(isDisabledAddTag ? 'disabled' : '')]"
        @click="showInput"
      >
        {{ addTagText }}
      </ll-tag>
      <ll-input
        v-else
        ref="inputRef"
        v-model:value.trim="inputValue"
        :placeholder="placeholder"
        size="small"
        :style="{ width: `${inputWidth}px` }"
        :class="[bem.e('input')]"
        @blur="handleInputConfirm"
        @keyup.enter="handleInputConfirm"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { CheckableTagItem, checkableTagProps } from './checkable-tag'

import { createNamespace } from '@ll-plus/utils'
import { cloneDeep } from 'lodash-es'

defineOptions({ name: 'LlCheckableTag' })

const props = defineProps(checkableTagProps)
const emits = defineEmits(['update:value', 'change'])

const bem = createNamespace('checkable-tag')

const inputRef = ref()
const inputValue = ref('')
const inputVisible = ref(false)
const newTags = ref([] as CheckableTagItem[])

const labelKey = computed(() => props.fieldNames?.label || 'label')
const valueKey = computed(() => props.fieldNames?.value || 'value')

const newValue = computed({
  get() {
    return Array.isArray(props.value) ? props.value : []
  },
  set(val) {
    emits('update:value', val)
    emits('change', val)
  },
})

// 新增标签是否禁用
const isDisabledAddTag = computed(() => {
  return (
    props.disabled ||
    props.disabledAddTag ||
    (props.maxAddableTags && newTags.value.length >= props.maxAddableTags) ||
    (props.selectionMode === 'multiple' &&
      props.addAutoSelect &&
      props.maxChecked &&
      newValue.value.length >= props.maxChecked)
  )
})

// 标签是否选中
const isChecked = computed(() => {
  return (tag: CheckableTagItem) => {
    return newValue.value.includes(tag[valueKey.value])
  }
})

// 显示关闭图标
const showCloseIcon = computed(() => {
  return (idx: number) => {
    if (props.deleteRule === 'all') {
      return true
    } else if (props.deleteRule === 'none') {
      return false
    } else if (props.deleteRule === 'exceptFirstN') {
      return idx >= props.exceptCount
    }
    return false
  }
})

// 关闭标签
const handleClose = (tag: CheckableTagItem) => {
  if (props.disabled || props.disabledCloseTag) return

  // 从展示列表中移除（基于 value）
  newTags.value = newTags.value.filter(
    (item) => item[valueKey.value] !== tag[valueKey.value],
  )

  // 是否存在选中项（基于 value）
  if (newValue.value.includes(tag[valueKey.value])) {
    newValue.value = newValue.value.filter(
      (item) => item !== tag[valueKey.value],
    )
  }
}

// 选中/取消标签
const handleCheck = (tag: CheckableTagItem, checked: boolean) => {
  if (props.disabled) return

  // 单选
  if (props.selectionMode === 'single') {
    newValue.value = checked ? [tag[valueKey.value]] : []
    return
  }

  // 多选
  if (
    checked &&
    props.maxChecked &&
    newValue.value.length >= props.maxChecked
  ) {
    return
  }

  newValue.value = checked
    ? [...newValue.value, tag[valueKey.value]]
    : newValue.value.filter((item) => item !== tag[valueKey.value])
}

// 显示输入框
const showInput = () => {
  if (isDisabledAddTag.value) return

  inputVisible.value = true
  nextTick(() => {
    inputRef.value.focus()
  })
}

// 确认输入框
const handleInputConfirm = () => {
  const val = inputValue.value.trim()

  if (val && !newTags.value.some((t) => t[valueKey.value] === val)) {
    // 新增展示标签
    newTags.value.push({ [labelKey.value]: val, [valueKey.value]: val } as any)

    // 自动选中
    if (props.addAutoSelect) {
      if (props.selectionMode === 'single') {
        newValue.value = [val]
      } else {
        newValue.value = [...newValue.value, val]
      }
    }
  }

  inputVisible.value = false
  inputValue.value = ''
}

const mergeTags = (tags: CheckableTagItem[], value: string[]) => {
  if (!Array.isArray(value)) return tags

  const existValues = new Set(tags.map((t) => t[valueKey.value]))
  const result = [...tags]

  value.forEach((v) => {
    // 不存在才补齐
    if (!existValues.has(v)) {
      result.push({ [labelKey.value]: v, [valueKey.value]: v } as any)
      existValues.add(v)
    }
  })

  return result
}

watch(
  () => props.options,
  (options) => {
    const baseTags = cloneDeep(options)

    newTags.value = mergeTags(baseTags, props.value)
  },
  { immediate: true, deep: true },
)

watch(
  () => props.value,
  (value) => {
    const next = mergeTags(newTags.value, value)

    // 只有真的新增才赋值
    if (next.length !== newTags.value.length) {
      newTags.value = next
    }
  },
  { immediate: true, deep: true },
)
</script>
